import { LoadData } from '@app/components/various/loaders/LoadData/LoadData';
import { LoadMore } from '@app/components/various/loaders/LoadMore';
import { Menu, MenuItem } from '@app/components/various/Menu';
import { compiledPaths } from '@app/paths';
import { useInfiniteSelectionsList } from '@app/services/hooks/orders/useInfiniteSelectionsList';
import { useAvailableOrderDetails } from '@app/utils/hooks/useAvailableOrderDetails';
import { isDefined } from '@app/utils/is';
import { isEmpty } from '@app/utils/isEmpty';
import { Key } from '@app/utils/keys';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

interface Props {
  search?: string;
  onTab?: (event: React.KeyboardEvent) => void;
  onOptionClick?: () => void;
}

export const OrderSelectMenu = ({ search = '', onTab, onOptionClick }: Props) => {
  const { t } = useTranslation(['common']);

  const { orderId } = useAvailableOrderDetails();
  const {
    data: selectionPages,
    fetchNextPage,
    isLoading,
    isFetching,
    isFetchingNextPage,
    hasNextPage,
    isPlaceholderData,
  } = useInfiniteSelectionsList({ search });

  const focusedItemRef = React.useRef<HTMLAnchorElement>(null);
  const [focusedItemKey, setFocusedItemKey] = React.useState<string | null>(orderId);

  const selections = React.useMemo(() => {
    if (!isDefined(selectionPages?.pages)) {
      return [];
    }

    return selectionPages.pages.flatMap(page => page.orders);
  }, [selectionPages]);

  React.useEffect(() => {
    setFocusedItemKey(orderId);
  }, [orderId]);

  React.useEffect(() => {
    focusedItemRef.current?.focus();
  }, [focusedItemKey]);

  const handleArrowUpKey = (event: React.KeyboardEvent) => {
    event.preventDefault();

    const index = selections.findIndex(({ order }) => order === focusedItemKey);

    if (index > 0) {
      setFocusedItemKey(selections[index - 1]?.order ?? null);
    }
  };

  const handleArrowDownKey = (event: React.KeyboardEvent) => {
    event.preventDefault();

    const index = selections.findIndex(({ order }) => order === focusedItemKey);

    if (index < selections.length - 1) {
      setFocusedItemKey(selections[index + 1]?.order ?? null);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    const handlers: Record<string, Maybe<(event: React.KeyboardEvent) => void>> = {
      [Key.UP]: handleArrowUpKey,
      [Key.DOWN]: handleArrowDownKey,
      [Key.TAB]: onTab,
    };

    handlers[event.code]?.(event);
  };

  const shouldShowLoader = isLoading || (isPlaceholderData && isFetching && !isFetchingNextPage);
  const shouldShowEmptyState = !isFetching && isEmpty(selections);

  return (
    <>
      {shouldShowLoader ?
        <LoadMore isLoading />
      : <Menu role="listbox" onKeyDown={handleKeyDown}>
          {selections.map(({ order, orderNumber, name, buyerFirstName, buyerLastName, accountName }) => (
            <MenuItem
              key={order}
              ref={order === focusedItemKey ? focusedItemRef : undefined}
              as={Link}
              to={compiledPaths.PRODUCTS_ORDER({ id: order })}
              isFocused={order === focusedItemKey}
              isSelected={order === orderId}
              role="option"
              onClick={onOptionClick}
            >
              <span className="flex gap-2">
                <span className="body-1">#{orderNumber}</span>
                {!isEmpty(name) && <span className="truncate">{name}</span>}
                {(!isEmpty(buyerFirstName) || !isEmpty(buyerLastName)) && (
                  <span data-sentry-mask className="truncate text-neutral-40">
                    {buyerFirstName} {buyerLastName}
                  </span>
                )}
                <span data-sentry-mask className="truncate text-neutral-40">
                  ({accountName})
                </span>
              </span>
            </MenuItem>
          ))}
          {hasNextPage && <LoadData isLoading={isFetching} onLoadMore={fetchNextPage} />}
          {shouldShowEmptyState && (
            <span role="presentation" className="mt-10 p-2 text-center body-2">
              {t('common:no_results')}
            </span>
          )}
        </Menu>
      }
    </>
  );
};
